// <!-- LEGAL REVIEW REQUIRED --> Template only. Placeholders are in LEGAL_TODO.md.
import React from 'react';
import { Link } from 'react-router-dom';
import { LegalList, LegalPage } from '../../components/ui';
import {
  COMPANY, CONTACT_EMAIL, LEGAL_UPDATED, MAILING_ADDRESS, REFUND_WINDOW, SHIP_WINDOW,
} from '../../lib/legal';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: `By using the Korsa website and services you agree to these Terms of Service. If you do not agree, please do not use them. These terms are between you and ${COMPANY}, a Delaware corporation.`,
  },
  {
    title: '2. Minimum Age',
    body: 'You must be at least 18 years old to place a pre-order or create an account.',
  },
  {
    title: '3. Not a Medical Device',
    body: 'Korsa Glasses and KorsaOS are consumer communication tools. They are not medical devices or hearing aids. They are not intended to diagnose, treat, cure, or prevent any disease or condition, and they do not replace care from a qualified professional. Captions, translations, suggestions, and answers are generated automatically and can be wrong or incomplete. Do not rely on them for medical, legal, safety, or emergency decisions.',
  },
  {
    title: '4. Using the Glasses Around Other People',
    body: 'Korsa Glasses use a microphone and may pick up the voices of people near you. Some places, including Massachusetts, require everyone in a conversation to consent before it is recorded. You are responsible for following the laws that apply to you and for telling people when needed.',
  },
  {
    title: '5. Website License',
    body: (
      <>
        <p className="text-zinc-600 leading-relaxed">
          You may view the materials on this website for personal, non-commercial use. You may not:
        </p>
        <LegalList
          items={[
            'Copy or modify the materials, or use them for a commercial purpose',
            'Reverse engineer any software on this website',
            'Remove any copyright or proprietary notices',
          ]}
        />
      </>
    ),
  },
  {
    title: '6. Product Information',
    body: 'Korsa Glasses are still in development. Features, specifications, and images may change before shipping. Product images on this website are renders unless we say otherwise.',
  },
  {
    // Linked as /terms#pre-orders from the order page, Stripe checkout and emails.
    id: 'pre-orders',
    title: '7. Pre-orders',
    body: (
      <>
        <p className="text-zinc-600 leading-relaxed">
          These terms apply when you pre-order Korsa Glasses. Our <Link to="/refunds" className="text-zinc-900 underline underline-offset-2">Refund and Cancellation Policy</Link> also applies.
        </p>
        <LegalList
          items={[
            'What you are buying: a pre-order for Korsa Glasses, a product still in development. Your pre-order reserves a pair for you, and pre-orders ship in the order they were placed.',
            'What you pay: the full price when you pre-order, $500 USD for non-prescription lenses or $600 USD with prescription lenses. Any applicable sales tax is shown on the Stripe checkout page before you pay. No further payment is due when your glasses ship.',
            'Price changes: we will not charge you more than you agreed to pay at checkout without your agreement. If the price for your pre-order would ever increase, we will tell you first, and you can either accept the new price or cancel for a full refund.',
            `Estimated ship window: ${SHIP_WINDOW}, for US deliveries first. This is an estimate, not a guaranteed date.`,
            'If shipping is delayed: in line with the FTC Mail, Internet, or Telephone Order Merchandise Rule, if we cannot ship within the estimated window we will email you before it passes with a revised estimate. You can then cancel for a full refund or agree to the new date.',
            `Cancelling: you can cancel for a full refund any time before your glasses ship, for any reason. Email ${CONTACT_EMAIL}. We issue refunds within ${REFUND_WINDOW}, to your original payment method.`,
            'Changes to the product: features and specifications may change before shipping, and product images are renders. If we make a significant change to what you ordered, we will tell you before shipping and you can cancel for a full refund.',
            'Prescription lenses: we will contact you for your prescription details before your glasses ship. Nothing is needed when you order.',
            'If we do not ship: if we cancel the product or cannot fulfill your pre-order for any reason, we will refund you in full.',
          ]}
        />
      </>
    ),
  },
  {
    title: '8. Software, Services and Subscriptions',
    body: (
      <>
        <p className="text-zinc-600 leading-relaxed">
          Korsa Glasses ship with KorsaOS. Some capabilities depend on services that cost us money to
          run continuously, including cloud processing, speech and language models, and third-party
          services. So that you know where you stand before you buy:
        </p>
        <LegalList
          items={[
            'Many KorsaOS features, including captions, translation, word help, and Ask, rely on cloud speech and AI services that cost us money every time you use them. Today they are included with your purchase at no extra cost.',
            'Korsa may in the future charge a subscription for features that rely on these cloud services, including features that were available when you bought your glasses, if the cost of running them requires it. Capabilities added after your purchase, including abilities offered through the marketplace, may also require a subscription.',
            'Anything your glasses and app can do without our paid cloud services will stay available without a subscription.',
            'You will never be enrolled in a subscription automatically or charged without agreeing first. Before any feature you use starts to require a subscription, we will give you at least 30 days notice by email.',
            'If we ever cannot continue operating a service your device relies on, we will tell you in advance and describe what will keep working without it.',
          ]}
        />
      </>
    ),
  },
  {
    title: '9. Disclaimer of Warranties',
    body: 'To the fullest extent the law allows, this website and its content are provided "as is" and "as available", without warranties of any kind. The glasses themselves come with the limited warranty described in our Refunds and Cancellation policy at korsa.tech/refunds. Nothing in these terms limits rights that cannot be waived under the law where you live.',
  },
  {
    title: '10. Limitation of Liability',
    body: `To the fullest extent the law allows, ${COMPANY} will not be liable for indirect, incidental, special, consequential, or punitive damages. Our total liability for any claim will not exceed the amount you paid us in the 12 months before the claim arose. Some places do not allow these limits, so they may not apply to you.`,
  },
  {
    title: '11. Changes to These Terms',
    body: 'We may revise these terms and will post the new version here. Where a revision materially changes what you receive for a purchase you already made, including any charge described in section 8, we will notify you by email in advance rather than relying on your continued use of the site as agreement.',
  },
  {
    title: '12. Governing Law',
    body: 'These terms are governed by the laws of the Commonwealth of Massachusetts, without regard to conflict of law rules. Any dispute will be heard in the state or federal courts located in Middlesex County, Massachusetts, unless the law where you live gives you the right to bring it in your local courts. Nothing in this section takes away consumer protections that apply where you live.',
  },
  {
    title: '13. Contact',
    body: `${COMPANY}, ${MAILING_ADDRESS}. Email: ${CONTACT_EMAIL}.`,
  },
];

const Terms = () => <LegalPage title="Terms of Service" updated={LEGAL_UPDATED} sections={SECTIONS} />;

export default Terms;
